import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, GraduationCap } from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'
import { categoryMeta, type Course } from './Learning'

const emptyForm = {
  title: '',
  description: '',
  category: 'technical',
  format: 'online',
  durationHours: '',
  capacity: '',
  instructorName: '',
  certValidityMonths: '',
}

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

export default function CourseForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const location = useLocation()
  const allowManage = hasAnyRole('admin', 'hr')

  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isEdit || !allowManage) return
    const fromState = (location.state as { course?: Course } | null)?.course
    const apply = (c: Course) => {
      setForm({
        title: c.title,
        description: c.description ?? '',
        category: c.category,
        format: c.format,
        durationHours: c.durationHours ? String(c.durationHours) : '',
        capacity: c.capacity ? String(c.capacity) : '',
        instructorName: c.instructorName ?? '',
        certValidityMonths: c.certValidityMonths ? String(c.certValidityMonths) : '',
      })
      setLoading(false)
    }
    if (fromState) {
      apply(fromState)
      return
    }
    apiJson<Course>(`/courses/${id}`)
      .then(apply)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load course')
        setLoading(false)
      })
  }, [id, isEdit, allowManage, location.state])

  const save = async () => {
    setSaving(true)
    setError('')
    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      description: form.description,
      category: form.category,
      format: form.format,
      durationHours: form.durationHours ? parseInt(form.durationHours, 10) : 0,
      capacity: form.capacity ? parseInt(form.capacity, 10) : 0,
    }
    if (form.instructorName.trim()) payload.instructorName = form.instructorName.trim()
    if (form.certValidityMonths) payload.certValidityMonths = parseInt(form.certValidityMonths, 10)
    try {
      if (isEdit) {
        await apiJson(`/courses/${id}`, { method: 'PATCH', body: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' } })
      } else {
        await apiJson('/courses', { method: 'POST', body: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' } })
      }
      navigate('/learning')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only HR or admin users can manage courses.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link to="/learning" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to Learning
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <GraduationCap size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{isEdit ? 'Edit Course' : 'New Course'}</h1>
            <p className="text-xs text-slate-400">
              {isEdit ? 'Changes apply to the catalog immediately' : 'Created as draft — publish it from the Manage tab'}
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div>
            <label className={labelCls}>Title *</label>
            <input required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Workplace Safety Fundamentals" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              rows={5}
              maxLength={10000}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What will learners cover in this course?"
              className={`${inputCls} resize-y`}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                {Object.entries(categoryMeta).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Format</label>
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className={inputCls}>
                <option value="online">Online</option>
                <option value="classroom">Classroom</option>
                <option value="webinar">Webinar</option>
                <option value="blended">Blended</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Duration (hours)</label>
              <input type="number" min={0} max={10000} value={form.durationHours} onChange={(e) => setForm({ ...form, durationHours: e.target.value })} placeholder="0 = self-paced" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Capacity</label>
              <input type="number" min={0} max={100000} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="0 = unlimited" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Instructor</label>
              <input maxLength={200} value={form.instructorName} onChange={(e) => setForm({ ...form, instructorName: e.target.value })} placeholder="Optional" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Certificate validity (months)</label>
              <input type="number" min={1} max={120} value={form.certValidityMonths} onChange={(e) => setForm({ ...form, certValidityMonths: e.target.value })} placeholder="Empty = no certificate" className={inputCls} />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to="/learning"
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
